import { Command } from "commander";
import chalk from "chalk";
import { getNetworkFromUrl } from "@crunchdao/sdk";
import { ConfigManager } from "../config";
import { CrunchConfig } from "../config/types";
import { createTable } from "../utils/table-utils";

export function addConfigCommands(program: Command) {
  const configCmd = program.command("config").description("Manage CLI configuration");

  configCmd
    .command("show")
    .description("Show current configuration file")
    .action(() => {
      const config = new ConfigManager();
      const currentConfig = config.load();
      const { profiles, ...configWithoutProfiles } = currentConfig;

      console.log(chalk.cyan.bold(`\nCLI Configuration`));
      console.log(chalk.gray("═".repeat(90)));

      const settingTable = createTable({ colWidths: [25, 65] });
      Object.entries(configWithoutProfiles).forEach(([key, value]) => {
        settingTable.push([chalk.yellow(key), chalk.grey(value)]);
      });
      console.log(settingTable.toString());

      console.log(chalk.cyan.bold(`\n\nProfiles`));
      console.log(chalk.gray("═".repeat(90)));

      const profileTable = createTable({ colWidths: [20, 70] });
      Object.entries(profiles).forEach(([key, value]) => {
        profileTable.push([chalk.yellow(key), chalk.grey(JSON.stringify(value, null, 2))]);
      });
      console.log(profileTable.toString());
    });

  configCmd
    .command("active")
    .description("Show resolved/active configuration values")
    .action(() => {
      const config = new ConfigManager();
      const globalConfig = config.load();

      // Show config file values with source indication
      const defaults = {
        url: "mainnet-beta",
        wallet: "~/.config/solana/id.json",
        loglevel: "info",
        output: null,
        multisigAddress: null,
      };

      console.log("Configuration Resolution:");
      console.log("═".repeat(60));

      // Helper function to show source
      const showValue = (key: string, displayName: string) => {
        const configValue = globalConfig[key as keyof CrunchConfig];
        const defaultValue = defaults[key as keyof typeof defaults];

        if (configValue !== undefined && configValue !== defaultValue) {
          console.log(`${displayName.padEnd(15)} ${configValue} (from config)`);
        } else {
          console.log(`${displayName.padEnd(15)} ${defaultValue || "none"} (default)`);
        }
      };

      showValue("url", "URL:");

      // Special handling for network
      const url = globalConfig.url || defaults.url;
      const network = getNetworkFromUrl(url);
      console.log(`${"Network:".padEnd(15)} ${network} (derived from URL)`);

      showValue("wallet", "Wallet:");
      showValue("loglevel", "Log Level:");
      showValue("output", "Output Format:");
      showValue("multisigAddress", "Multisig:");

      console.log("\nNote: CLI options (--url, --wallet, etc.) override these values");
      console.log("Priority: CLI options > config file > defaults");

      // Show active profile hint if any profiles exist
      if (globalConfig.profiles && Object.keys(globalConfig.profiles).length > 0) {
        console.log(`\nAvailable profiles: ${Object.keys(globalConfig.profiles).join(", ")}`);
        console.log("Use 'config use-profile <name>' to apply profile settings");
      }
    });

  configCmd
    .command("set")
    .description("Set configuration value")
    .argument("<key>", "Configuration key (url, wallet, loglevel)")
    .argument("<value>", "Configuration value")
    .action((key: string, value: string) => {
      const config = new ConfigManager();
      try {
        config.set(key, value);
        console.log(`Set ${key} = ${value}`);
      } catch (error) {
        console.error(error instanceof Error ? error.message : "Unknown error");
        process.exit(1);
      }
    });

  configCmd
    .command("use")
    .description("Switch to a configuration profile")
    .argument("<profile>", "Profile name")
    .action((profile: string) => {
      const config = new ConfigManager();
      try {
        config.useProfile(profile);
        console.log(`Switched to profile: ${profile}`);
      } catch (error) {
        console.error(error instanceof Error ? error.message : "Unknown error");
        process.exit(1);
      }
    });

  configCmd
    .command("list-profiles")
    .description("List available configuration profiles")
    .action(() => {
      const config = new ConfigManager();
      const profiles = config.listProfiles();
      if (profiles.length === 0) {
        console.log("No profiles configured");
      } else {
        console.log("Available profiles:");
        profiles.forEach((profile) => console.log(`  - ${profile}`));
      }
    });

  configCmd
    .command("save-profile")
    .description("Save current configuration as a profile")
    .argument("<profile>", "Profile name to save")
    .action((profile: string) => {
      const config = new ConfigManager();
      try {
        config.saveCurrentAsProfile(profile);
        console.log(`Current configuration saved as profile: ${profile}`);
      } catch (error) {
        console.error(error instanceof Error ? error.message : "Unknown error");
        process.exit(1);
      }
    });

  configCmd
    .command("init")
    .description("Initialize configuration file with defaults")
    .action(() => {
      const config = new ConfigManager();
      config.initializeIfNeeded();
      console.log("Configuration initialized at ~/.crunch/config.json");
    });
}
